import { Button } from "antd"
import { ReactNode } from "react"

import ComponentErrorBoundary from "#blocks/ComponentErrorBoundary"
import CopyToClipboard from "#blocks/CopyToClipboard"
import Flex from "#blocks/Flex"
import Modal from "#blocks/Modal"
import { type Uuid } from "#customTypes/globalTypes"
import { StudentArtworkForDisplay } from "#forms/AddStudent/StudentArtworkSection"
import { useCanSeeIds } from "#hooks/newHooks/useHasPermission"

import PortfolioPhotoGrid from "./PortfolioPhotoGrid"

const demoArtLinks = [
  "https://firebasestorage.googleapis.com/v0/b/firestore-test-d50d6.firebasestorage.app/o/demoImages%2Fescapegoat2_asian_elementary_school_art_students_sculpture_of_5a291990-edce-4848-85a0-5113d7135898_0.png?alt=media&token=c59e55f4-8265-41c9-bf45-d025e3b9b1a9",
  "https://firebasestorage.googleapis.com/v0/b/firestore-test-d50d6.firebasestorage.app/o/demoImages%2Fescapegoat2_asian_elementary_school_art_students_sculpture_of_5a291990-edce-4848-85a0-5113d7135898_2.png?alt=media&token=d5e39fb8-2012-4550-bfdf-10bbf7196f40",
  "https://firebasestorage.googleapis.com/v0/b/firestore-test-d50d6.firebasestorage.app/o/demoImages%2Fescapegoat2_asian_high_school_girls_art_student_self_portrait_bd347f4a-02b9-41dc-a936-a9f919930362_1.png?alt=media&token=d004e73e-57dc-4dc0-a91e-876008c49ab4",
  "https://firebasestorage.googleapis.com/v0/b/firestore-test-d50d6.firebasestorage.app/o/demoImages%2Fescapegoat2_asian_high_school_girls_art_student_self_portrait_bd347f4a-02b9-41dc-a936-a9f919930362_3.png?alt=media&token=8ff442ea-32fc-4904-9337-e8fbb2140704",
] as const

const mockArtworks: StudentArtworkForDisplay[] = [
  {
    id: "art-1",
    timeCreated: 1710806400000,
    url: demoArtLinks[0],
    filename: "selected-ideas.jpg",
    className: "AP Art Studio",
    semester: "Spring 2026",
    classId: "class-ap-1",
    projectId: "project-1",
    inPortfolio: true,
    showAddToPortfolioCta: false,
    sortIndex: 0,
    rotation: 0,
  },
  {
    id: "art-2",
    timeCreated: 1711065600000,
    url: demoArtLinks[1],
    filename: "selected-process.jpg",
    className: "AP Art Studio",
    semester: "Spring 2026",
    classId: "class-ap-1",
    projectId: "project-1",
    inPortfolio: true,
    showAddToPortfolioCta: false,
    sortIndex: 1,
    rotation: 0,
  },
  {
    id: "art-3",
    timeCreated: 1711670400000,
    url: demoArtLinks[2],
    filename: "sustained-material-study.jpg",
    className: "AP Art Studio",
    semester: "Spring 2026",
    classId: "class-ap-1",
    projectId: "project-2",
    inPortfolio: true,
    showAddToPortfolioCta: false,
    sortIndex: 0,
    rotation: 0,
  },
  {
    id: "art-4",
    timeCreated: 1712102400000,
    url: demoArtLinks[3],
    filename: "sustained-revision.jpg",
    className: "AP Art Studio",
    semester: "Spring 2026",
    classId: "class-ap-1",
    projectId: "project-2",
    inPortfolio: true,
    showAddToPortfolioCta: false,
    sortIndex: 1,
    rotation: 0,
  },
]

const PortfolioManagementForm = ({
  studentId,
  initialProjectId,
}: {
  studentId?: string
  initialProjectId?: string
}) => {
  return (
    <ComponentErrorBoundary
      context="Portfolio Management Grid"
      fallbackStyle={{ minHeight: 420 }}
    >
      <Flex col style={{ height: "80vh", minHeight: 0 }}>
        <PortfolioPhotoGrid
          studentId={studentId != null ? (studentId as Uuid) : null}
          artworks={mockArtworks}
          initialProjectId={initialProjectId}
        />
      </Flex>
    </ComponentErrorBoundary>
  )
}

interface PortfolioManagementModalProps {
  width?: number
  isOpen?: boolean
  footer?: ReactNode
  handleCloseAttempt: () => void
  title: string
  initialValues?: { id?: string }
  initialProjectId?: string
}

const PortfolioManagementModal = ({
  width,
  isOpen,
  footer,
  handleCloseAttempt,
  title,
  initialValues,
  initialProjectId,
}: PortfolioManagementModalProps) => {
  const canSeeIds = useCanSeeIds()
  return (
    <Modal
      width={width || 1800}
      open={isOpen}
      footer={
        footer ?? [
          <Button key="close" onClick={handleCloseAttempt}>
            Close
          </Button>,
        ]
      }
      onClose={handleCloseAttempt}
      title={
        <Flex
          gap={8}
          style={{
            alignItems: "center",
            justifyContent: "space-between",
            marginRight: 30,
            fontSize: 18,
          }}
        >
          {title}
          {initialValues?.id && canSeeIds ? (
            <CopyToClipboard
              data={initialValues.id}
              label={`ID: ${initialValues.id}`}
            />
          ) : null}
        </Flex>
      }
    >
      <PortfolioManagementForm
        studentId={initialValues?.id}
        initialProjectId={initialProjectId}
      />
    </Modal>
  )
}

export default PortfolioManagementModal
